'use client';

import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { motion } from 'framer-motion';

interface ClientExecDomainSearchProps {
  className?: string;
}

export default function ClientExecDomainSearch({ className }: ClientExecDomainSearchProps) {
  const [domainName, setDomainName] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isClient, setIsClient] = useState(false); // Ensures hydration consistency

  useEffect(() => {
    setIsClient(true); // Mark component as client-side

    // Extract domain from URL on client-side
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const domainParam = urlParams.get('domain');
      if (domainParam) {
        setDomainName(domainParam);
      }
    }
  }, []);

  const handleSearch = () => {
    if (!domainName) return;
    setIsSearching(true);
    setError(null);

    // Validate domain format
    if (!domainName.includes('.')) {
      setError('Invalid domain. Please include a TLD (e.g., .com, .net)');
      setIsSearching(false);
      return;
    }

    // Extract domain name and TLD
    const parts = domainName.split('.');
    if (parts.length < 2) {
      setError('Invalid domain format');
      setIsSearching(false);
      return;
    }

    const name = parts.slice(0, -1).join('.'); // Everything before the last dot
    const tld = parts[parts.length - 1]; // Last part after the dot

    // Ensure window is available before redirecting
    if (typeof window !== 'undefined') {
      window.location.href = `https://panel.1dollarcloud.com/order.php?step=1&productGroup=2&search=1&domainName=${encodeURIComponent(name)}&tld=${encodeURIComponent(tld)}`;
    }
  };

  if (!isClient) {
    return null; // Avoid rendering during SSR
  }

  return (
    <div className={className}>
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="yourdomain.com"
            className="w-full px-6 py-4 rounded-lg bg-slate-800/80 border border-slate-700/50 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 backdrop-blur-sm"
            value={domainName}
            onChange={(e) => setDomainName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <motion.button
          className="bg-gradient-rainbow text-white px-8 py-4 rounded-lg font-medium shadow-neon flex items-center justify-center whitespace-nowrap disabled:opacity-100"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSearch}
          disabled={isSearching || !domainName}
        >
          {isSearching ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
          ) : (
            <Search className="mr-2" />
          )}
          {isSearching ? 'Searching...' : 'Search Domain'}
        </motion.button>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400"
        >
          {error}
        </motion.div>
      )}
    </div>
  );
}
